import Image from "next/image";
import classnames from "classnames";
import { useRouter } from "next/router";
import { useWebTranslation } from "translations";
import { getOrg, Org } from "utils/site";
import styles from "./Awards.module.css";

// On these GER routes the Stiftung Warentest (Warentest) award is swapped for
// the TÜV Kundenzufriedenheit ("SEHR GUT") badge.
const GER_WARENTEST_SWAP_PATHS = [
  "/",
  "/katzenversicherung",
  "/katzenversicherung/katzen-op-versicherung",
  "/cat-insurance",
  "/cat-insurance/cat-surgery-insurance",
];

type AwardBadge = {
  src: string;
  alt: string;
  width: number;
  height: number;
  href?: string;
  // Artwork whose own aspect ratio differs from the badge box, so it has to be
  // fitted inside it rather than stretched to fill it.
  contain?: boolean;
};

const BADGE_WIDTH = 221;
const BADGE_HEIGHT = 90;

const badge = (
  src: string,
  alt: string,
  href?: string,
  contain?: boolean
): AwardBadge => ({
  src,
  alt,
  width: BADGE_WIDTH,
  height: BADGE_HEIGHT,
  href,
  contain,
});

// Press coverage, shown under the award badges where an org has them. Separate
// from AwardBadge: an award is a rating we hold, a press logo is a publication
// that wrote about us, so the two rows carry different labels and must not be
// merged.
type PressLogo = {
  src: string;
  alt: string;
  // Intrinsic size of the artwork. The row caps the rendered width and lets the
  // height follow, so these only fix the aspect ratio for next/image.
  width: number;
  height: number;
};

// GBR only. Every other org has no verified press assets, so it gets no strip.
const getPressLogosForOrg = (org: Org): PressLogo[] => {
  if (org !== "gbr") {
    return [];
  }

  return [
    {
      src: "/images/press/gbr/the-times.webp",
      alt: "The Times",
      width: 468,
      height: 240,
    },
    {
      src: "/images/press/gbr/financial-times.webp",
      alt: "Financial Times",
      width: 480,
      height: 240,
    },
    {
      src: "/images/press/gbr/fortune.webp",
      alt: "Fortune",
      width: 480,
      height: 240,
    },
    {
      src: "/images/press/gbr/independent.webp",
      alt: "The Independent",
      width: 480,
      height: 240,
    },
    {
      src: "/images/press/gbr/metro.webp",
      alt: "Metro",
      width: 480,
      height: 240,
    },
  ];
};

// GBR has no case: its Trustpilot and Defaqto marks are uploaded on the hero
// entry's `badges` field, so repeating them here would draw them twice on the
// same page. The press strip below is unaffected.
const getAwardBadgesForOrg = (org: Org, path: string): AwardBadge[] => {
  switch (org) {
    case "swe":
      return [
        badge("/images/badges/swe/konsumenternas.png", "Konsumenternas"),
        badge("/images/badges/swe/ski.png", "SKI", undefined, true),
      ];
    case "ger":
      return [
        badge(
          "/images/badges/ger/trustpilot.png",
          "Trustpilot",
          "https://www.trustpilot.com/review/lassie.co/location/deutschland"
        ),
        GER_WARENTEST_SWAP_PATHS.includes(path)
          ? badge("/images/badges/ger/stiftung.png", "Stiftung Warentest")
          : badge(
              "/images/badges/ger/tuv-kundenzufriedenheit-2026.png",
              "TÜV Kundenzufriedenheit",
              "https://www.tuv-saar.com/ID/46709"
            ),
        badge(
          "/images/badges/ger/tuv-preis-leistung-2026.png",
          "TÜV",
          "https://www.tuv-saar.com/ID/46710"
        ),
      ];
    case "fra":
      return [
        badge(
          "/images/badges/fra/trustpilot.png",
          "Trustpilot",
          "https://fr.trustpilot.com/review/lassie.co"
        ),
        badge("/images/badges/fra/googleplay.png", "Google Play"),
        badge("/images/badges/fra/appstore.png", "App Store"),
      ];
    default:
      return [];
  }
};

export const Awards = () => {
  const router = useRouter();
  const { t } = useWebTranslation();
  const org = getOrg();
  // Drop the query and hash: the static HTML is rendered without them, so an
  // ad click's "/?utm_source=…" must pick the same badges or hydration fails.
  const path = router.asPath.split(/[?#]/)[0];
  const badges = getAwardBadgesForOrg(org, path);
  const pressLogos = getPressLogosForOrg(org);

  if (badges.length === 0 && pressLogos.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      {badges.length > 0 && (
        <div className={styles.badgesRow}>
          {badges.map((badge) => {
            const image = (
              <Image
                src={badge.src}
                alt={badge.alt}
                width={badge.width}
                height={badge.height}
                className={badge.contain ? styles.containImage : undefined}
              />
            );

            return (
              <div key={badge.src} className={styles.badgeCard}>
                {badge.href ? (
                  <a
                    className={styles.badgeLink}
                    href={badge.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {image}
                  </a>
                ) : (
                  image
                )}
              </div>
            );
          })}
        </div>
      )}

      {pressLogos.length > 0 && (
        <div
          className={classnames(styles.pressStrip, {
            [styles.pressStripAlone]: badges.length === 0,
          })}
        >
          <p className={styles.pressLabel}>{t("awards.featured_in")}</p>
          <div className={styles.pressRow}>
            {pressLogos.map((logo) => (
              <Image
                key={logo.src}
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
